import { setAgentTyping } from "store/app/app.actions";
import AppTypes from "store/app/app.types";
import { MiddlewareStore } from "store/createStore";
import { IChatMessage } from "store/messages/messages.state";
import MessagesTypes from "store/messages/messages.types";
import { postMessageToParent, timeoutManager } from "utils";
import * as Logger from 'services/logger';
import { messageMap } from "services/messageMap";
import { SmartagentChatWebsocketType } from "@missionlabs/smartagent-bchat";
import { getChatTranscript, connectBChat, transformChatMessage, syncMessages } from "./chatSessionListenerUtils";
import { messageQueuer } from "./messageQueuer";
import { handleAttachment } from "./onMessage/handleAttachment";
import { handleParticipantJoined } from "./onMessage/handleParticipantJoined";
import { handleSmartagentMessage } from "./onMessage/handleSmartagentMessage";
import { setDeliveredTimestamp, setReadTimestamp } from "store/messages/messages.actions";
import getChatSession from "services/chatSession/getChatSession";


const listenToChatSession = async (session: connect.CustomerChatSession, store: MiddlewareStore, ws: SmartagentChatWebsocketType, restoreSession?: boolean) => {

    const displayMap = messageMap();

    const messageQueue = messageQueuer(store, session);
    let reconnected = false;

    const setTyping = timeoutManager({
        onCall: (agentName) => store.dispatch(setAgentTyping(true, agentName)),
        onTimeout: () => store.dispatch(setAgentTyping(false)),
        timeoutMs: 7000
    });

    session.onMessage(async (event) => {
        messageQueue.queue(event)
    })

    // @ts-ignore
    session.onDeliveredReceipt(async (event) => {
        const messageId = event.data.MessageMetadata.MessageId;
        const deliveredTimestamp: string | undefined = event.data?.MessageMetadata?.Receipts?.find((receipt: any) => !!receipt.DeliveredTimestamp)?.DeliveredTimestamp;

        if (!deliveredTimestamp) return;

        store.dispatch(setDeliveredTimestamp(messageId, deliveredTimestamp));
    });

    // @ts-ignore
    session.onReadReceipt(async (event) => {
        const messageId = event.data.MessageMetadata.MessageId
        const readTimestamp: string | undefined = event.data?.MessageMetadata?.Receipts?.find((receipt: any) => !!receipt.ReadTimestamp)?.ReadTimestamp

        if (!readTimestamp) return

        store.dispatch(setReadTimestamp(messageId, readTimestamp))
    })

    session.onTyping(({ data }) => {
        console.log('recieved typing event from connect', data);

        window.bchat.putChatEvent({
            contactId: session.getChatDetails().contactId,
            event: "typing"
        });

        if (data.ParticipantRole === "CUSTOMER") return;

        setTyping(data.DisplayName);
    });

    session.onConnectionEstablished(async ({ chatDetails }) => {
        console.log('connection established');
        if(reconnected) {
            reconnected = false;
            const {apiKey,websocketUrl} = store.getState().app.connectionDetails
            await connectBChat({apiKey,websocketUrl});
            const {restoreSession,globalRestoreSession,attachmentsConfig} = store.getState().app;
            const sessionParams = {
                ws:window.bchat,
                connectionDetails: store.getState().app.connectionDetails,
                store,
                restoreSession,
                globalRestoreSession,
                attachmentsEnabled: attachmentsConfig?.enabled,

            }
            await getChatSession(sessionParams);
            await syncMessages(session, store,messageQueue);
        };
        await window.bchat.postConnectionToken({
            contactId: chatDetails.initialContactId,
            connectionToken: chatDetails.connectionDetails?.connectionToken!
        })
    });

    session.onConnectionLost(() => {
        console.log('connection lost')
        reconnected = true;
    })

    session.onConnectionBroken(({ chatDetails }) => {
        console.log('connection to connect is broken');
        Logger.warn('connection broken', chatDetails)
        // TODO: reconnect logic: 
        // session.connect();
    });

    session.onEnded(async () => {
        console.log('chat ended');

        store.dispatch({
            type: AppTypes.SET_AGENT_CONNECTED,
            payload: false
        })

        const { customerDisconnectFlowId } = store.getState().app.connectionDetails
        const { customerDisconnectFlowStarted, customerEndedChat, ended } = store.getState().app

        try {
            if ((customerDisconnectFlowId && customerEndedChat && !customerDisconnectFlowStarted)) {
                // this is to prevent the chat ending fully if the customer ends the chat and a disconnect flow should be initiated. This then resets the customerEndedChat so it can be set correctly if the customer ends the chat while in the customerDisconnectFlowId flow
                console.log('sa-log reset customerEndedChat ', { customerDisconnectFlowId, customerEndedChat, customerDisconnectFlowStarted })
                store.dispatch({

                    type: AppTypes.SET_CUSTOMER_ENDED_CHAT,
                    payload: false
                })

                return
            }

            if (!ended) {
                console.log('sa-log dispatch END_CHAT')
                store.dispatch({
                    type: AppTypes.END_CHAT,
                    payload: {
                        preventClosingChatWindow: true
                    }
                });
                ws.close();
            }
            window.bchat.close();

            postMessageToParent(
                'delete-smartagent-persistance-item',
                'smartagent_connection/global'
            );

        } catch (error) {
            console.log('sa-log error in session.onEnded ', error)
            Logger.error('Error when chat ended', error)
            // this should catch any errors thrown and just end the chat. This makes sure the chat ends correctly when the autodisconnect happens. deleteChatSession fails on auto disconnect as the session is already deleted and they catch block should make sure 

            store.dispatch({
                type: AppTypes.END_CHAT,
            })
            window.bchat.close();
            
            postMessageToParent(
                'delete-smartagent-persistance-item',
                'smartagent_connection/global'
            );
        } finally {
            displayMap.clear();
        }
    });

    if (!restoreSession) return

    const rawTranscript = await getChatTranscript(session);
    const processedTranscript: IChatMessage[] = [];
    const transcript: IChatMessage[] = []

    let currentReceipt: any;


    for (const m of rawTranscript) {
        const message =
            await transformChatMessage(m, store, session)
                .then((message) => handleAttachment(message, store, session, false))
                .then((message) => handleParticipantJoined(message, store))
                .then((message) => handleSmartagentMessage(message, store))

        processedTranscript.push(message as IChatMessage);

    }

    for (const message of processedTranscript.reverse()) {

        const receipt = {
            DeliveredTimestamp: message.deliveredTimestamp,
            ReadTimestamp: message.readTimestamp
        };

        if (receipt) {
            currentReceipt = receipt;
        }

        if (Array.isArray(message)) {
            for (const m2 of message) {
                if (currentReceipt) {
                    m2.deliveredTimestamp = currentReceipt.DeliveredTimestamp;
                    m2.readTimestamp = currentReceipt.ReadTimestamp;
                }

                !m2.discarded && transcript.unshift(m2)
            }

            continue;
        }

        if (currentReceipt) {
            message.deliveredTimestamp = currentReceipt.DeliveredTimestamp;
            message.readTimestamp = currentReceipt.ReadTimestamp;
        }

        const displayAs = displayMap.get(message.id);

        if (displayAs) {
            message.content = displayAs;
        }

        if (!message.discarded) {
            transcript.unshift(message)
        }
    }

    if (transcript.length) {
        store.dispatch({
            type: MessagesTypes.NEW_MESSAGE,
            payload: transcript
        })
    }

}

export default listenToChatSession;